import { usePageInfo, useStateRef } from '@op/hooks/common';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { PageInfoType } from '@op/types/common';
import { DBO_TM } from '@op/utils/format';
import { gotoDownload } from '@opm/pages/Download';
import { getDouyinVideoList } from '@opm/services';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SortBy } from 'tea-component/lib/table/addons';
import { DOUYIN_DEFAULT_SORT } from '../tabs/DouyinTab/constants';

function getListData(data: any[][]) {
  const result: any[] = [];
  let page = 1;
  data.every(item => {
    if (item) {
      result.push(...item);
      page += 1;
      return true;
    }
    return false;
  });
  return { page, result };
}

/** Douyin list 响应 → VideoTable / VideoCard 通用字段 */
function mapDouyinVideoItem(item: any) {
  const engagement =
    (item.like_count || 0) +
    (item.share_count || 0) +
    (item.comment_count || 0);

  return {
    ...item,
    tweets_view: item.view_count,
    tweets_like: item.like_count,
    tweets_comment: item.comment_count,
    video_release_time: item.publish_time,
    anchor_name: item.author_name,
    channel_name: 'douyin',
    engagement,
    views: item.view_count,
    likes: item.like_count,
    comments: item.comment_count,
    username: item.author_name,
    cover: item.video_cover,
    time: item.publish_time,
    channel: 'douyin',
    url: item.video_url,
    duration: item.video_duration,
  };
}

interface IUseDouyinVideoDataParams {
  filter: any;
  defaultPageInfo?: { page: number; page_size: number };
}

export function useDouyinVideoData({
  filter,
  defaultPageInfo = { page: 1, page_size: 40 },
}: IUseDouyinVideoDataParams) {
  const [keywordRef, keyword, setKeyword] = useStateRef<string>('');
  const [sortByRef, sortBy, setSortBy] = useStateRef<SortBy<string>>({
    order: DOUYIN_DEFAULT_SORT.order,
    by: DOUYIN_DEFAULT_SORT.by,
  });
  const {
    pageInfo,
    changePageInfo,
    resetPageInfo,
    nextPage,
    changePageInfoNoRender,
  } = usePageInfo({ defaultPageInfo });
  const [dataRef, data, setData] = useStateRef<any[][]>([]);
  const [total, setTotal] = useState(0);
  const [maxMetrics, setMaxMetrics] = useState<Record<string, number>>({});
  const { wrapperCancelRequest, loading, loadingRef } = useCancelRequestHook();
  const [operation, setOperation] = useState<'card' | 'table'>('card');

  function resetData() {
    setData([]);
    setMaxMetrics({});
    resetPageInfo();
  }

  function changeSortBy(sortByValue: SortBy<string>) {
    setSortBy(sortByValue);
    resetData();
  }

  function changeKeyword(keywordValue: string) {
    if (keywordValue === keywordRef.current) return;
    setKeyword(keywordValue);
    resetData();
  }

  const params = useMemo(() => {
    if (!filter) return {};
    const { categories, channels, date_type, ...rest } = filter;
    const sortKey = sortByRef.current?.by || DOUYIN_DEFAULT_SORT.by;
    const payload: Record<string, any> = {
      start_time: rest.start_time,
      end_time: rest.end_time,
      ...pageInfo,
      sort_item: sortKey,
      sort_model: sortByRef.current?.order || 'desc',
    };

    if (categories !== undefined) {
      payload.category = categories;
    }
    if (keywordRef.current) {
      payload.search_text = keywordRef.current;
    }
    return payload;
  }, [filter, pageInfo, sortBy, keyword]);

  function request() {
    if (!filter?.start_time) return;
    wrapperCancelRequest(getDouyinVideoList, params)
      .then((res: any) => {
        if (!res) return;
        const { list, total: nextTotal, max } = res;
        const realList = (list || []).map(mapDouyinVideoItem);

        if (dataRef.current) {
          dataRef.current[pageInfo.page - 1] = realList;
          setData([...dataRef.current]);
        }
        setTotal(nextTotal || 0);
        if (max) {
          setMaxMetrics(max);
        }
      })
      .catch(() => {
        const { page } = getListData(dataRef.current || []);
        console.log('page :: ', page);
        if (page > 0) {
          changePageInfoNoRender({ ...pageInfo, page: page - 1 });
        }
      });
  }

  useEffect(() => {
    if (!filter?.start_time) return;
    const { page: loadedPageCount } = getListData(dataRef.current || []);
    if (pageInfo.page < loadedPageCount) return;
    if (dataRef.current?.[pageInfo.page - 1]) return;
    request();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  function download() {
    if (!filter?.start_time) return;
    gotoDownload({
      key: 'MarketingDouyinVideoDownload',
      fileName: `Videos_Douyin_${DBO_TM.timeTitle(filter)}`,
      filter: params,
    });
  }

  function changePageInfoData(localPageInfo: PageInfoType) {
    if (pageInfo.page_size !== localPageInfo.page_size) {
      setData([]);
    }
    changePageInfo(localPageInfo);
  }

  const nextPageChange = useCallback(() => {
    // 同步读 ref，避免触底连触发时 closure 里 loading 仍为 false
    if (loadingRef.current) return;
    const { page, page_size } = pageInfo;
    if (total <= page * page_size) return;
    if (operation === 'table') return;
    nextPage();
  }, [loadingRef, pageInfo, total, operation, nextPage]);

  function changeOperation(value: 'card' | 'table') {
    setOperation(value);
    if (value === 'table') {
      changePageInfoNoRender({ ...pageInfo, page: 1 });
    } else {
      const { page } = getListData(dataRef.current || []);
      changePageInfoNoRender({ ...pageInfo, page: page - 1 });
    }
  }

  const cardRecords = useMemo(() => getListData(data || []).result, [data]);
  const tableRecords = useMemo(
    () => data?.[pageInfo.page - 1] || [],
    [data, pageInfo],
  );

  return {
    data,
    records: cardRecords,
    cardRecords,
    tableRecords,
    total,
    pageInfo,
    changePageInfo: changePageInfoData,
    nextPage: nextPageChange,
    loading,
    sortBy,
    keyword,
    setKeyword: changeKeyword,
    setSortBy: changeSortBy,
    download,
    operation,
    setOperation: changeOperation,
    resetData,
    max: maxMetrics,
    maxMetrics,
  };
}
